/**
 * Iter51 Etapa 13 — ActivityFeed (shared between SaleDetail & ClientDetail tabs).
 *
 * Renders a chronological timeline (newest first) of:
 *   - audit events (manual edits, status overrides, withdraw/clear, etc.)
 *   - field_change events (per-field old → new diffs from FieldHistory)
 *   - status_change events (Sale-only)
 *
 * Props:
 *   - entityType: "sale" | "client"
 *   - entityId: UUID
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Activity, RefreshCw, FileEdit, Workflow, Upload, ShieldAlert, Ban, Undo2,
  ArrowRight, User as UserIcon, Clock, FileText,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =========================== source theming ===========================
const SOURCE_THEME = {
  salesforce: { bg: "bg-purple-50", fg: "text-purple-700", ring: "ring-purple-200", label: "Salesforce" },
  sf_sync:    { bg: "bg-purple-50", fg: "text-purple-700", ring: "ring-purple-200", label: "SF Sync" },
  import_salesforce: { bg: "bg-purple-50", fg: "text-purple-700", ring: "ring-purple-200", label: "Import SF" },
  import_easy:{ bg: "bg-blue-50",   fg: "text-blue-700",   ring: "ring-blue-200",   label: "Import Easy" },
  easy:       { bg: "bg-blue-50",   fg: "text-blue-700",   ring: "ring-blue-200",   label: "Easy" },
  import_crm: { bg: "bg-indigo-50", fg: "text-indigo-700", ring: "ring-indigo-200", label: "Import CRM" },
  crm_legacy: { bg: "bg-indigo-50", fg: "text-indigo-700", ring: "ring-indigo-200", label: "CRM legacy" },
  crm:        { bg: "bg-indigo-50", fg: "text-indigo-700", ring: "ring-indigo-200", label: "CRM legacy" },
  manual:     { bg: "bg-slate-50",  fg: "text-slate-700",  ring: "ring-slate-200",  label: "Manual" },
  bulk:       { bg: "bg-amber-50",  fg: "text-amber-700",  ring: "ring-amber-200",  label: "Bulk" },
};
const themeForSource = (src) => SOURCE_THEME[src] || SOURCE_THEME.manual;

// =========================== icons per kind/action ===========================
const iconForEvent = (ev) => {
  const a = (ev.action || "").toLowerCase();
  if (ev.kind === "status_change") return Workflow;
  if (ev.kind === "field_change") return FileEdit;
  if (ev.kind === "import") return Upload;
  if (a.includes("withdraw") && a.includes("clear")) return Undo2;
  if (a.includes("withdraw") || a.includes("renunta")) return Ban;
  if (a.includes("import")) return Upload;
  if (a.includes("delete")) return ShieldAlert;
  if (a.includes("status")) return Workflow;
  return RefreshCw;
};

// =========================== date formatter ===========================
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ro-RO", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// =========================== one event row ===========================
const EventRow = ({ event, isFirst, isLast }) => {
  const Icon = iconForEvent(event);
  const theme = themeForSource(event.source);
  const isFieldChange = event.kind === "field_change";
  const oldV = event.details?.old;
  const newV = event.details?.new;

  return (
    <li className="relative pl-12 pb-5 last:pb-0" data-testid={`activity-event-${event.kind}`}>
      {/* vertical connector */}
      {!isLast && <span className="absolute left-[18px] top-9 bottom-0 w-px bg-slate-200" />}
      {/* icon bubble */}
      <div className={`absolute left-0 top-0 h-9 w-9 rounded-full ${theme.bg} ${theme.fg} ring-2 ${theme.ring} flex items-center justify-center`}>
        <Icon className="h-4 w-4" />
      </div>
      {/* card */}
      <div className="bg-white border border-slate-200 rounded-md p-3 shadow-sm">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">
              {event.label || event.action || "Eveniment"}
            </p>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
              <Clock className="h-3 w-3" />
              <span>{fmtDateTime(event.when)}</span>
              {event.actor_name && (
                <>
                  <span className="text-slate-300">·</span>
                  <UserIcon className="h-3 w-3" />
                  <span data-testid="activity-event-actor">{event.actor_name}</span>
                </>
              )}
              <Badge className={`${theme.bg} ${theme.fg} border-0 text-[10px] px-1.5 py-0`}>
                {theme.label}
              </Badge>
              {event.entity_type && (
                <span className="text-[10px] text-slate-400 uppercase tracking-wide">
                  on {event.entity_type}
                </span>
              )}
              {event.kind === "import" && (
                <Badge
                  className={`${event.reconstructed ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"} border-0 text-[10px] px-1.5 py-0`}
                  title={event.reconstructed
                    ? "Eveniment derivat din metadatele vânzării (first_imported_at + lot); lotul nu a înregistrat rezultatul rândului."
                    : "Eveniment înregistrat de import la momentul operațiunii (rezultat pe rând, salvat pe lot)."}
                  data-testid={event.reconstructed ? "activity-import-reconstructed" : "activity-import-recorded"}
                >
                  {event.reconstructed ? "reconstruit din metadate" : "înregistrat la import"}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* import event: lot + fișier */}
        {event.kind === "import" && event.details && (
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-600 flex-wrap" data-testid="activity-import-details">
            {event.details.filename && <span className="font-mono">{event.details.filename}</span>}
            {event.batch_id && (
              <Link to={`/import?batch=${event.batch_id}`} className="font-mono text-blue-600 hover:underline" title="Deschide lotul în pagina Import">
                lot {event.batch_id.slice(0, 8)}
              </Link>
            )}
            {event.details.batch_status && <span className="text-slate-400">· lot: {event.details.batch_status}</span>}
          </div>
        )}

        {/* field change diff */}
        {isFieldChange && (oldV !== undefined || newV !== undefined) && (
          <div className="mt-2 flex items-center gap-2 text-xs flex-wrap" data-testid="activity-event-diff">
            <span className="px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-100 line-through max-w-[260px] truncate" title={oldV || "—"}>
              {oldV ?? <i>—</i>}
            </span>
            <ArrowRight className="h-3 w-3 text-slate-400" />
            <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100 max-w-[260px] truncate" title={newV || "—"}>
              {newV ?? <i>—</i>}
            </span>
          </div>
        )}

        {/* status change pretty */}
        {event.kind === "status_change" && event.details && (
          <div className="mt-2 flex items-center gap-2 text-xs">
            <Badge className="bg-slate-100 text-slate-700 border-0 text-[11px]">{event.details.old_status || "—"}</Badge>
            <ArrowRight className="h-3 w-3 text-slate-400" />
            <Badge className="bg-blue-100 text-blue-700 border-0 text-[11px]">{event.details.new_status}</Badge>
          </div>
        )}

        {/* generic audit details (compact) */}
        {event.kind === "audit" && event.details && Object.keys(event.details).length > 0 && (
          <details className="mt-2 group" data-testid="activity-event-details">
            <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">Detalii tehnice</summary>
            <pre className="mt-1 text-[10px] bg-slate-50 border border-slate-100 rounded p-2 overflow-x-auto max-h-40">
{JSON.stringify(event.details, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </li>
  );
};

// =========================== main component ===========================
const ActivityFeed = ({ entityType, entityId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActivity = async () => {
    setLoading(true);
    setError(null);
    try {
      const path = entityType === "client"
        ? `${API_URL}/clients/${entityId}/activity?limit=200`
        : `${API_URL}/sales/${entityId}/activity?limit=200`;
      const r = await axios.get(path);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Eroare la încărcarea activității");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!entityId) return;
    fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  // =========== render ===========
  return (
    <div className="space-y-4" data-testid="activity-feed">
      {/* Header card */}
      <Card className="shadow-sm border-slate-200">
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center">
              <Activity className="h-5 w-5 text-emerald-700" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Activitate</p>
              <p className="text-base font-semibold text-slate-900">
                {data?.total ?? 0} {data?.total === 1 ? "eveniment" : "evenimente"}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchActivity} disabled={loading} data-testid="activity-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Reîncarcă
          </Button>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-3 text-sm text-red-800" data-testid="activity-error">{error}</CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading && !data && (
        <Card className="shadow-sm border-slate-200">
          <CardContent className="p-6 text-center text-sm text-slate-500" data-testid="activity-loading">
            Se încarcă activitatea...
          </CardContent>
        </Card>
      )}

      {/* Empty */}
      {!loading && data && data.total === 0 && (
        <Card className="shadow-sm border-slate-200">
          <CardContent className="p-8 text-center" data-testid="activity-empty">
            <FileText className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-2 text-sm text-slate-500">Nu există încă evenimente înregistrate pentru această entitate.</p>
            <p className="text-xs text-slate-400 mt-1">
              Activitatea include: editări manuale, sincronizări Salesforce, modificări de status, importuri.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      {!loading && data && data.total > 0 && (
        <Card className="shadow-sm border-slate-200">
          <CardContent className="p-5">
            <ol data-testid="activity-timeline">
              {data.events.map((ev, i) => (
                <EventRow
                  key={`${ev.kind}-${ev.when}-${i}`}
                  event={ev}
                  isFirst={i === 0}
                  isLast={i === data.events.length - 1}
                />
              ))}
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ActivityFeed;
